"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { useCart } from "@/context/CartContext";
// MOW-PERF-OKT26: de lade (27 KB + zijn eigen code) wordt pas gemount bij het eerste openen, niet op elke
// pagina. De chunk wordt wel rustig voorgeladen (zie useLaadNaInteractie in Header), zodat het eerste
// openen niet op het netwerk hoeft te wachten.
const laadCartDrawer = () => import("./CartDrawer");
const CartDrawer = dynamic(laadCartDrawer, { ssr: false });
import UspSlider from "./UspSlider";
import { t } from "@/lib/site-texts";
import { useLaadNaInteractie } from "@/lib/use-load-after-intent"; // MOW-PERF-OKT26
import { laadMenu } from "@/lib/navigation-client"; // MOW-CATEGORIEFILTER-OKT26

type MenuLink = { label: string; href: string; count?: number };
type MenuKolom = { title: string; href?: string; links: MenuLink[] };
// >>> MOW-NOV26 black-friday: `accent` = opvallende menulink (de Black Friday-pagina, alleen zolang die online is)
type MenuItem = { label: string; href: string; columns?: MenuKolom[]; accent?: boolean };
const ACCENT_NAV_STIJL: React.CSSProperties = { color: "var(--color-primary)", fontWeight: 700 };
// <<< MOW-NOV26 black-friday

/**
 * Terugval zolang /api/navigation nog niet geladen is (of onbereikbaar blijkt).
 * Bewust alleen de hoofditems: het echte menu — inclusief subcategorieën, merken
 * en producttellingen — komt uit de data, zodat het niet meer achter kan lopen
 * op het assortiment.
 */
const FALLBACK_NAV: MenuItem[] = [
  { label: "Dames", href: "/product-category/dames" },
  { label: "Heren", href: "/product-category/heren" },
  { label: "Kinderen", href: "/product-category/kinderen" },
  { label: "Nachtmode", href: "/product-category/nachtmode" },
  { label: "Thermo", href: "/product-category/thermo" },
  // >>> MOW-OKT26 cmsquick: Unisex erbij, zodat de server-header dezelfde 8 items heeft als /api/navigation (geen sprong na hydratatie)
  { label: "Unisex", href: "/product-category/unisex" },
  // <<< MOW-OKT26 cmsquick
  { label: "Merken", href: "/merken" },
  { label: "Sale", href: "/shop?sale=1" },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { cart, openCart, closeCart, isOpen } = useCart();
  const { itemCount, total: totalPrice } = cart;
  // >>> MOW-PERF-OKT26
  // De lade bestaat pas vanaf het eerste openen en blijft daarna gemount (de maat/kleur-kiezer en de
  // focusafhandeling horen bij dezelfde instantie). Het script komt na de eerste aanraking, of anders
  // ±4 s na het laden, alvast binnen; het wordt dan nog niet uitgevoerd of getekend.
  const [ladeGemount, setLadeGemount] = useState(false);
  if (isOpen && !ladeGemount) setLadeGemount(true); // state aanpassen tijdens het renderen: meteen mounten, geen extra frame
  useLaadNaInteractie(() => { void laadCartDrawer(); });
  // <<< MOW-PERF-OKT26
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const menuTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Menu uit de data; tot die er is tonen we de hoofditems.
  const [navItems, setNavItems] = useState<MenuItem[]>(FALLBACK_NAV);
  useEffect(() => {
    let afgebroken = false;
    (async () => {
      try {
        // MOW-CATEGORIEFILTER-OKT26: gedeelde belofte, het categoriefilter leest dezelfde route
        const items = await laadMenu();
        if (!afgebroken && items.length) setNavItems(items as MenuItem[]);
      } catch { /* terugval blijft staan */ }
    })();
    return () => { afgebroken = true; };
  }, []);

  // Zoekopdracht → /shop?zoek= (dezelfde route die het menu al gebruikt).
  const submitSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = searchQuery.trim();
    if (!q) { searchInputRef.current?.focus(); return; }
    setSearchOpen(false);
    setMobileOpen(false);
    router.push(`/shop?zoek=${encodeURIComponent(q)}`);
  };

  // Verlaat de bezoeker de zoekresultaten (/shop), dan de oude zoekterm wissen —
  // anders staat "sokken" nog in het veld op de homepage na een eerdere zoekactie.
  useEffect(() => {
    if (pathname !== "/shop") {
      setSearchQuery("");
      setSearchOpen(false);
    }
  }, [pathname]);

  // Bij openen: focus het veld; Escape sluit.
  useEffect(() => {
    if (searchOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 30);
      const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") setSearchOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
    }
  }, [searchOpen]);

  const handleMenuEnter = (label: string) => {
    if (menuTimeout.current) clearTimeout(menuTimeout.current);
    setActiveMenu(label);
  };

  const handleMenuLeave = () => {
    menuTimeout.current = setTimeout(() => setActiveMenu(null), 120);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);

  // Storefront-header verbergen in het beheerpaneel
  if (pathname?.startsWith("/admin")) return null;

  return (
    <>
      {/* Aanbieding topbalk — automatische USP-slider */}
      <UspSlider />

      {/* Hoofd header — position:sticky creates positioning context for mega menu */}
      <header
        className="site-header"
        style={{ boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.08)" : undefined }}
      >
        <div className="container">
          <div className="header-inner">
            {/* Logo */}
            <Link href="/" className="header-logo" aria-label="Mijnondergoedwinkel.nl - Home" style={{ padding: "5px 0" }}>
              <Image
                src="/images/logo-klein.png"
                data-i-key="global.logo"
                alt="Mijnondergoedwinkel.nl"
                width={186}
                height={62}
                priority
                className="header-logo-img"
              />
            </Link>

            {/* Desktop Navigatie — triggers only; mega panels are siblings of .container */}
            <nav className="header-nav" aria-label="Hoofdnavigatie">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="nav-item-wrapper"
                  onMouseEnter={() => item.columns ? handleMenuEnter(item.label) : setActiveMenu(null)}
                  onMouseLeave={handleMenuLeave}
                >
                  <Link
                    href={item.href}
                    style={item.accent ? ACCENT_NAV_STIJL : undefined}
                    className={`nav-link${activeMenu === item.label ? " hovered" : ""}${pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href)) ? " active" : ""}`}
                  >
                    {item.label}
                    {item.columns && <span className="nav-chevron" aria-hidden="true">&#8964;</span>}
                  </Link>
                </div>
              ))}
            </nav>

            {/* Acties rechts */}
            <div className="header-actions">
              {/* Zoeken — loepje klapt een zoekveld uit (desktop inline, mobiel volle-breedte balk) */}
              <div className={`header-search${searchOpen ? " open" : ""}`}>
                <form onSubmit={submitSearch} className="header-search-form">
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => { if (!searchQuery.trim()) setSearchOpen(false); }}
                    placeholder={t("header.search.placeholder", "Waar ben je naar op zoek?")}
                    aria-label="Zoeken"
                    tabIndex={searchOpen ? 0 : -1}
                  />
                </form>
                <button
                  type="button"
                  onClick={() => (searchOpen ? submitSearch() : setSearchOpen(true))}
                  aria-label="Zoeken"
                  aria-expanded={searchOpen}
                  className="header-search-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </button>
              </div>
              {/* Account */}
              {/* MOW-PERF-OKT26: prefetch={false}. Next haalde de routebestanden van /account (en daarmee de
                  volledige Firestore- en Auth-SDK, ±230 KB gzip) ±1 s na het laden op elke pagina binnen,
                  ook voor bezoekers die nooit inloggen. Bij een klik laadt de route nu op dat moment. */}
              <Link
                href="/account"
                prefetch={false}
                aria-label="Mijn account"
                className="header-account-link"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </Link>
              {/* Winkelwagen knop */}
              <button
                onClick={openCart}
                className="cart-button"
                aria-label={`Winkelwagen: ${itemCount} artikel(en), ${formatPrice(totalPrice)}`}
                id="header-cart-btn"
              >
                <span className="cart-price" style={{ fontSize: "0.875rem" }}>{formatPrice(totalPrice)}</span>
                <span className="cart-count">{itemCount}</span>
                {/* Winkelwagen icoon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
              </button>

              {/* Hamburger mobiel */}
              <button
                className="mobile-menu-btn"
                onClick={() => setMobileOpen(true)}
                aria-label="Menu openen"
                aria-expanded={mobileOpen}
                id="mobile-menu-btn"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mega menu panels — direct children of <header> so position:absolute spans full width */}
        {navItems.map((item) => !item.columns ? null : (
          <div
            key={item.label}
            className={`mega-menu${activeMenu === item.label ? " mega-menu--open" : ""}`}
            onMouseEnter={() => handleMenuEnter(item.label)}
            onMouseLeave={handleMenuLeave}
          >
            <div className="container">
              <div
                className="mega-menu-inner"
                style={{ display: "grid", gridTemplateColumns: `repeat(${item.columns.length}, 1fr)`, gap: "32px" }}
              >
                {item.columns.map((col, idx) => (
                  <div key={idx} className="mega-menu-column">
                    <h3>
                      {col.href
                        ? <Link href={col.href} onClick={() => setActiveMenu(null)} style={{ color: "inherit", textDecoration: "none" }}>{col.title}</Link>
                        : col.title}
                    </h3>
                    <ul>
                      {col.links.map((link) => (
                        <li key={link.label}>
                          <Link href={link.href} onClick={() => setActiveMenu(null)}>{link.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </header>

      {/* Mobiel menu drawer */}
      <div
        className={`mobile-drawer${mobileOpen ? " open" : ""}`}
        aria-hidden={!mobileOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Mobiel menu"
      >
        <div className="mobile-drawer-overlay" onClick={() => setMobileOpen(false)} />
        <div className="mobile-drawer-panel">
          {/* Sluitknop */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
            <Link href="/" className="header-logo" style={{ fontSize: "0.9rem" }}>
              <span className="logo-line1">mijn</span>
              <span className="logo-line2" style={{ fontSize: "1.1rem" }}>ondergoedwinkel.nl</span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              style={{ color: "var(--color-gray-600)", padding: "4px" }}
              aria-label="Menu sluiten"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Navigatie links */}
          <nav aria-label="Mobiele navigatie" style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <Link
              href="/"
              style={{
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid var(--color-gray-100)",
                fontSize: "0.95rem",
                fontWeight: pathname === "/" ? 600 : 400,
                color: pathname === "/" ? "var(--color-primary)" : "var(--color-secondary)",
                textDecoration: "none",
              }}
            >
              <span data-t-key="header.mobile.home">{t("header.mobile.home", "Home")}</span>
            </Link>
            {navItems.map((item) => {
              const hasSub = !!item.columns;
              const isExpanded = expandedItem === item.label;
              return (
                <div key={item.label} style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Link
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      style={{
                        display: "block",
                        padding: "12px 0",
                        fontSize: "0.95rem",
                        fontWeight: item.accent ? 700 : pathname.startsWith(item.href) ? 600 : 400,
                        color: item.accent || pathname.startsWith(item.href) ? "var(--color-primary)" : "var(--color-secondary)",
                        textDecoration: "none",
                        flex: 1,
                      }}
                    >
                      {item.label}
                    </Link>
                    {hasSub && (
                      <button
                        onClick={() => setExpandedItem(isExpanded ? null : item.label)}
                        style={{ padding: "12px", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-500)", display: "flex", alignItems: "center" }}
                        aria-label={isExpanded ? "Submenu inklappen" : "Submenu uitklappen"}
                      >
                        <span style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", fontSize: "0.8rem" }}>▼</span>
                      </button>
                    )}
                  </div>
                  {hasSub && isExpanded && (
                    <div style={{ padding: "0 0 12px 14px", background: "#fbfbfa", display: "flex", flexDirection: "column", gap: "8px" }}>
                      {item.columns!.map((col, cIdx) => (
                        <div key={cIdx} style={{ marginTop: "6px" }}>
                          {col.href ? (
                            <Link
                              href={col.href}
                              onClick={() => setMobileOpen(false)}
                              style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "var(--color-gray-400)", marginBottom: "4px", textDecoration: "none" }}
                            >
                              {col.title}
                            </Link>
                          ) : (
                            <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "var(--color-gray-400)", marginBottom: "4px" }}>
                              {col.title}
                            </div>
                          )}
                          {col.links.map((link) => (
                            <Link
                              key={link.label}
                              href={link.href}
                              onClick={() => setMobileOpen(false)}
                              style={{
                                display: "block",
                                padding: "6px 0",
                                fontSize: "0.85rem",
                                color: "var(--color-gray-600)",
                                textDecoration: "none",
                              }}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              href="/over-ons"
              onClick={() => setMobileOpen(false)}
              style={{
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid var(--color-gray-100)",
                fontSize: "0.95rem",
                fontWeight: pathname === "/over-ons" ? 600 : 400,
                color: pathname === "/over-ons" ? "var(--color-primary)" : "var(--color-secondary)",
                textDecoration: "none",
              }}
            >
              <span data-t-key="header.mobile.over-ons">{t("header.mobile.over-ons", "Over ons")}</span>
            </Link>
            <Link
              href="/blog-inspiratie"
              onClick={() => setMobileOpen(false)}
              style={{
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid var(--color-gray-100)",
                fontSize: "0.95rem",
                fontWeight: pathname === "/blog-inspiratie" ? 600 : 400,
                color: pathname === "/blog-inspiratie" ? "var(--color-primary)" : "var(--color-secondary)",
                textDecoration: "none",
              }}
            >
              <span data-t-key="header.mobile.blog-inspiratie">{t("header.mobile.blog-inspiratie", "Blog & Inspiratie")}</span>
            </Link>
            <Link
              href="/contact-ons"
              onClick={() => setMobileOpen(false)}
              style={{
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid var(--color-gray-100)",
                fontSize: "0.95rem",
                fontWeight: pathname === "/contact-ons" ? 600 : 400,
                color: pathname === "/contact-ons" ? "var(--color-primary)" : "var(--color-secondary)",
                textDecoration: "none",
              }}
            >
              <span data-t-key="header.mobile.contact-ons">{t("header.mobile.contact-ons", "Contact ons")}</span>
            </Link>
          </nav>

          {/* Contact info */}
          <div style={{ marginTop: "24px", fontSize: "0.8rem", color: "var(--color-gray-500)" }}>
            <p>📧 info@mijnondergoedwinkel.nl</p>
            <p style={{ marginTop: "6px" }}>📞 +31 6 23399503</p>
          </div>
        </div>
      </div>

      {/* Cart Drawer Panel */}
      {ladeGemount && <CartDrawer isOpen={isOpen} onClose={closeCart} />}
    </>
  );
}
